'use client';

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthState, AuthContextType, User } from '@/types/auth';
import { loginUser, registerUser, registerOrganization as registerOrganizationAPI, verifyToken, getCurrentUser } from '@/lib/api/auth';
import { useLocalStorage } from '@/lib/hooks/useLocalStorage';

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS' }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'ORGANIZATION_REGISTER_START' }
  | { type: 'ORGANIZATION_REGISTER_SUCCESS' }
  | { type: 'ORGANIZATION_REGISTER_FAILURE'; payload: string }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
    case 'ORGANIZATION_REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
    case 'ORGANIZATION_REGISTER_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'REGISTER_SUCCESS':
    case 'ORGANIZATION_REGISTER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the context
export { AuthContext };

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [storedToken, setStoredToken, isHydrated] = useLocalStorage<string | null>('authToken', null);

  // Check for existing token on mount (client-side only)
  useEffect(() => {
    const checkAuth = async () => {
      // Wait for hydration to complete
      if (!isHydrated) {
        return;
      }

      try {
        if (storedToken) {
          console.log('🔍 [Auth Context] Found existing token, getting user profile...');
          const user = await getCurrentUser(storedToken);
          console.log('🔍 [Auth Context] User data from API:', user);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { user, token: storedToken },
          });
        } else {
          console.log('🔍 [Auth Context] No existing token found');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('❌ [Auth Context] Token verification failed:', error);
        setStoredToken(null);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, [storedToken, isHydrated]); // Removed setStoredToken from dependencies

  // Login function
  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      console.log('🔍 [Auth Context] Starting login...');
      
      const response = await loginUser({ email, password });
      
      // Store token using custom hook
      setStoredToken(response.accessToken);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          token: response.accessToken,
        },
      });
      
      console.log('✅ [Auth Context] Login successful');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('❌ [Auth Context] Login failed:', errorMessage);
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Register function
  const register = async (userData: any) => {
    try {
      dispatch({ type: 'REGISTER_START' });
      console.log('🔍 [Auth Context] Starting registration...');
      
      await registerUser(userData);
      
      dispatch({ type: 'REGISTER_SUCCESS' });
      console.log('✅ [Auth Context] Registration successful');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      console.error('❌ [Auth Context] Registration failed:', errorMessage);
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const registerOrganization = async (organizationData: any) => {
    try {
      dispatch({ type: 'ORGANIZATION_REGISTER_START' });
      console.log('🔍 [Auth Context] Starting organization registration...');
      
      await registerOrganizationAPI(organizationData);
      
      dispatch({ type: 'ORGANIZATION_REGISTER_SUCCESS' });
      console.log('✅ [Auth Context] Organization registration successful');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Organization registration failed';
      console.error('❌ [Auth Context] Organization registration failed:', errorMessage);
      dispatch({ type: 'ORGANIZATION_REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    console.log('🔍 [Auth Context] Logging out...');
    setStoredToken(null);
    dispatch({ type: 'LOGOUT' });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Update user function
  const updateUser = (user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const value: AuthContextType = {
    ...state,
    isLoading: !isHydrated || state.isLoading, // Show loading until hydrated
    login,
    register,
    registerOrganization,
    updateUser,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
